// src/common11/commonModals/CommonModal.jsx
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import CommonButton from "../Buttons/CommonButton";
import PrintButton from "../Buttons/PrintButton";
import CancelDialogIconButton from "../Buttons/CancelIconButton";

const CommonModal = ({
  isOpen,
  onClose,
  title,
  children,
  DialogContent: CustomContent,
  actions,
  size = "md",
  fullWidth = true,
  showCloseButton = true,
  disableBackdropClose = true, // close only via cross icon or action buttons, not on outside click or Escape
  disableScroll = false,
  maxHeightVh = 95,
  paperSx = {},
}) => {
  const sizeMapping = {
    xs: "xs",
    sm: "sm",
    md: "md",
    lg: "lg",
    xl: "xl",
  };

  return (
    <Dialog
      open={isOpen}
      onClose={(event, reason) => {
        if (
          disableBackdropClose &&
          (reason === "backdropClick" || reason === "escapeKeyDown")
        ) {
          return;
        }
        if (onClose) onClose();
      }}
      maxWidth={sizeMapping[size]}
      fullWidth={fullWidth}
      aria-labelledby="modal-title"
      PaperProps={{
        sx: {
          borderRadius: "8px",
          margin: { xs: "8px", lg: "32px" },
          maxHeight: `${maxHeightVh}vh`,
          width: { xs: "calc(100% - 16px)", sm: "auto" },
          maxWidth: { xs: "calc(100vw - 16px)", lg: "none" },
          ...paperSx,
        },
      }}
    >
      {/* Header - Compact Padding */}
      {(title || showCloseButton) && (
        <DialogTitle
          id="modal-title"
          sx={{
            m: 0,
            p: { xs: "8px 12px", sm: "10px 16px" },
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid #e5e7eb",
            minHeight: { xs: "40px", sm: "44px" },
          }}
        >
          {title && (
            <span className="text-base sm:text-lg font-semibold text-[#217E90] flex-1 truncate">
              {title}
            </span>
          )}
          {showCloseButton && (
            <CancelDialogIconButton
              onClick={onClose}
              size="xs"
              className="flex-shrink-0 ml-2"
            />
          )}
        </DialogTitle>
      )}

      {/* Content - Compact Padding & Optimized Max Height */}
      <DialogContent
        dividers
        sx={{
          ...(disableScroll
            ? {
                overflow: "hidden",
                overflowY: "hidden",
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
              }
            : {
                maxHeight: {
                  xs: `calc(${maxHeightVh}vh - 100px)`,
                  sm: `calc(${maxHeightVh}vh - 110px)`,
                },
                overflowY: "auto",
                "&::-webkit-scrollbar": { width: "6px" },
                "&::-webkit-scrollbar-track": { background: "#f1f1f1" },
                "&::-webkit-scrollbar-thumb": {
                  background: "#888",
                  borderRadius: "3px",
                },
                "&::-webkit-scrollbar-thumb:hover": { background: "#555" },
              }),
          p: { xs: "14px", sm: "20px", md: "24px" },
        }}
      >
        {CustomContent || children}
      </DialogContent>

      {/* Footer - Compact Padding */}
      {actions && actions.length > 0 && (
        <DialogActions
          sx={{
            p: { xs: "8px 12px", sm: "10px 16px" },
            gap: { xs: "4px", sm: "8px" },
            borderTop: "1px solid #e5e7eb",
            justifyContent: "flex-end",
            flexWrap: { xs: "wrap", sm: "nowrap" },
          }}
        >
          {actions.map((action, index) =>
            action.isPrintButton ? (
              <PrintButton
                key={index}
                onClick={action.onClick}
                label={action.label}
                className={action.className}
                disabled={action.disabled}
                type={action.type || "button"}
                size="sm"
              />
            ) : (
              <CommonButton
                key={index}
                onClick={action.onClick}
                label={action.label}
                className={action.className}
                disabled={action.disabled}
                icon={action.icon}
                iconPosition={action.iconPosition}
                variant={(() => {
                  if (Object.prototype.hasOwnProperty.call(action, "variant")) {
                    return action.variant ? action.variant : undefined;
                  }
                  // Last action is primary (e.g. single "Book Appointment" stays success-filled)
                  return index === actions.length - 1
                    ? "success-filled"
                    : "reset-outlined";
                })()}
                type={action.type || "button"}
                size="sm"
              />
            ),
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default CommonModal;
